import { TFunction } from 'i18next';
import { UserAccount } from '../components/blocks/UserAccountList/types';
import { isUser } from '../components/blocks/UserAccountList/utils';
import { BASE_URL, IDENTITY_PATH, IS_DEV, SERVICE_URL_TEMPLATE, USER_URL } from './config';
import { secondsInDay, secondsInHour, secondsInMonth, secondsInYear } from './constants';

export const isEmail = (value: string) => {
  // eslint-disable-next-line
  const regexEmail = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexEmail.test(value);
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const capitalizeFirstLetter = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const isEmptyString = (value?: string) => (value ? value.trim().length === 0 : true);

export const getQueryParams = (url: string): Record<string, string> => {
  const results: Record<string, string> = {};
  const queryParams = url.split('?');
  if (queryParams.length > 1) {
    const params = queryParams[1].split('&');

    for (let i = 0, len = params.length; i < len; i += 1) {
      const parts = params[i].split('=');
      if (parts.length > 1) {
        const [key, value] = parts;
        results[key] = value;
      }
    }
  }
  return results;
};

export const mapQueryString = (payload: Record<string, unknown>): string =>
  Object.entries(payload)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

export const addQueryParam = (
  url: string,
  payload: Record<string, string>,
): string => {
  let queryParams = getQueryParams(url);
  queryParams = {
    ...queryParams,
    ...payload,
  };
  const queryString = mapQueryString(queryParams);
  const baseUrl = url.split('?')[0];
  return `${baseUrl}?${queryString}`;
};

const fallbackCopyTextToClipboard = (text: string) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  
  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand('copy');
  } catch {
    console.warn('Unable to copy text to clipboard');
  } finally {
    document.body.removeChild(textArea);
  }
};

export const copyTextToClipboard = async (text: string) => {
  if (!navigator.clipboard) {
    // Asynchronous Clipboard API not supported, fallback to document.execCommand('copy') which
    // is deprecated for modern browsers
    fallbackCopyTextToClipboard(text);
    return;
  }

  // Try to use the Asynchronous Clipboard API
  // Support at 91.46% as of May 6th, 2022: https://caniuse.com/async-clipboard
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    console.warn('Unable to copy text to clipboard');
  }
};

export const formatLastLogin = (date: Date, t: TFunction): string => {
  const timeDif = Math.floor((Date.now() - new Date(date).getTime()) / 1000);    // Time difference in seconds

  let interval = timeDif / secondsInYear;

  if (interval >= 1) {
    return t('last_login.year', { count: Math.floor(interval) });
  }
  interval = timeDif / secondsInMonth;
  if (interval >= 1) {
    return t('last_login.month', { count: Math.floor(interval) });
  }
  interval = timeDif / secondsInDay;
  if (interval >= 1) {
    return t('last_login.day', { count: Math.floor(interval) });
  }
  interval = timeDif / secondsInHour;
  if (interval >= 1) {
    return t('last_login.hour', { count: Math.floor(interval) });
  }
  return t('last_login.less_than_hour');
};

export const extractUrlDetails = (url: string): {
  appId?: string;
  serviceName?: string;
} => {
  const serviceUrlRegexTemplate = (SERVICE_URL_TEMPLATE as string)
    .replace('{appId}', '([a-zA-Z0-9]*)')
    .replace('{serviceName}', '([a-zA-Z0-9]*)');

  const serviceUrlRegex = new RegExp(serviceUrlRegexTemplate);
  const results = serviceUrlRegex.exec(url);

  if (results) {
    const appId = results[1];
    const serviceName = results[2];
    return {
      appId,
      serviceName,
    };
  }
  return {};
};

export const getServerUrl = (): string => {
  if (IS_DEV) {
    return USER_URL;
  }

  const { appId } = extractUrlDetails(window.location.pathname);
  return `${BASE_URL}/${appId}/${IDENTITY_PATH}`;
};

export const parseSlug = (slug?: string) => {
  if (slug) {
    return slug.charAt(0).toUpperCase() + slug.replace('-', ' ').slice(1);
  }
};

export const displayUserInfo = (user?: UserAccount) => {
  if (user && isUser(user)) {
    const fullName = `${user.firstName} ${user.lastName}`;
    return !isEmptyString(fullName) ? fullName : user.email;
  } else {
    return user?.email;
  }
};

export const downloadFile = (content: string, fileName: string, type: string) => {
  const blob = new Blob([content], { type: type });
  const csvURL = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = csvURL;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const convertToCSVColumns = (columns: string[]): string => {
  return columns.map(column => `"${column}"`).join(',');
};